import { Head, useForm, Link } from '@inertiajs/react'
import SuperAdminLayout from '../_SuperAdminLayout'
import { sortPeople } from '../../../utils/peopleSort'

interface GuideOption { id: number; email: string; full_name: string }

interface Props {
  errors?: Record<string, string | string[]>
  available_guides?: GuideOption[]
  assignable_roles?: string[]
}

type GuideMode = 'none' | 'existing' | 'new'

interface AdditionalUser {
  email: string
  first_name: string
  last_name: string
  role: string
  password: string
}

const ROLE_LABELS: Record<string, string> = {
  observer: 'Observer',
  member: 'Member',
  admin: 'Admin',
  account_owner: 'Account Owner',
  guide: 'Guide'
}

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  let out = ''
  const arr = new Uint32Array(14)
  crypto.getRandomValues(arr)
  arr.forEach(n => { out += chars[n % chars.length] })
  return out
}

function err(errors: Record<string, string | string[]>, key: string): string | undefined {
  const v = errors[key]
  if (!v) return undefined
  return Array.isArray(v) ? v.join(', ') : v
}

export default function TenantsNew({ errors = {}, available_guides = [], assignable_roles = ['observer', 'member', 'admin', 'account_owner', 'guide'] }: Props) {
  const { data, setData, post, processing } = useForm({
    send_welcome_emails: false,
    tenant: { name: '', slug: '', timezone: 'UTC' },
    admin: { email: '', first_name: '', last_name: '', role: 'account_owner' },
    guide: { mode: 'none' as GuideMode, account_id: '', first_name: '', last_name: '', email: '', password: '' },
    users: [] as AdditionalUser[]
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/super_admin/tenants')
  }

  const baseErr = err(errors, 'base')
  const guideErr = err(errors, 'guide')

  const setGuide = (patch: Partial<typeof data.guide>) => setData('guide', { ...data.guide, ...patch })

  const addUser = () => setData('users', [...data.users, { email: '', first_name: '', last_name: '', role: 'member', password: '' }])
  const removeUser = (idx: number) => setData('users', data.users.filter((_, i) => i !== idx))
  const setUser = (idx: number, patch: Partial<AdditionalUser>) =>
    setData('users', data.users.map((u, i) => (i === idx ? { ...u, ...patch } : u)))

  return (
    <SuperAdminLayout>
      <Head title="Create tenant" />
      <div className="max-w-2xl">
        <Link href="/super_admin/tenants" className="text-sm text-[#ED1C24] hover:underline">&larr; All tenants</Link>
        <h1 className="text-2xl font-bold text-text-primary mt-2 mb-4">Create tenant</h1>

        {baseErr && <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 rounded text-sm">{baseErr}</div>}

        <form onSubmit={submit} className="bg-white border border-stone/20 rounded-lg p-6 space-y-6">
          <fieldset>
            <legend className="text-base font-semibold text-text-primary mb-2">Organization</legend>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required value={data.tenant.name} onChange={v => setData('tenant', { ...data.tenant, name: v })} />
              <Field label="Slug (optional)" value={data.tenant.slug} onChange={v => setData('tenant', { ...data.tenant, slug: v })} hint="Auto-generated from name if blank" />
              <Field label="Timezone" value={data.tenant.timezone} onChange={v => setData('tenant', { ...data.tenant, timezone: v })} hint="IANA timezone, such as America/New_York. Default: UTC." />
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-base font-semibold text-text-primary mb-2">First admin</legend>
            <div className="grid grid-cols-2 gap-4">
              <Field label="First name" value={data.admin.first_name} onChange={v => setData('admin', { ...data.admin, first_name: v })} />
              <Field label="Last name" value={data.admin.last_name} onChange={v => setData('admin', { ...data.admin, last_name: v })} />
              <Field label="Email" required type="email" value={data.admin.email} onChange={v => setData('admin', { ...data.admin, email: v })} />
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Role</label>
                <select
                  value={data.admin.role}
                  onChange={e => setData('admin', { ...data.admin, role: e.target.value })}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg"
                >
                  <option value="account_owner">Account Owner</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-text-muted mt-3">An invitation will be created for this address. Email is optional below; the user sets their own password upon acceptance.</p>
          </fieldset>

          <fieldset>
            <legend className="text-base font-semibold text-text-primary mb-2">Guide</legend>
            {guideErr && <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 rounded text-sm">{guideErr}</div>}
            <div className="flex flex-wrap gap-4 mb-3">
              {(['none', 'existing', 'new'] as GuideMode[]).map(mode => (
                <label key={mode} className="flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="radio"
                    name="guide_mode"
                    checked={data.guide.mode === mode}
                    onChange={() => setGuide({ mode })}
                  />
                  {mode === 'none' ? 'No guide' : mode === 'existing' ? 'Assign existing guide' : 'Create new guide'}
                </label>
              ))}
            </div>

            {data.guide.mode === 'existing' && (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Select guide</label>
                {available_guides.length === 0 ? (
                  <p className="text-sm text-text-muted">No existing guides. Choose "Create new guide" instead.</p>
                ) : (
                  <select
                    value={data.guide.account_id}
                    onChange={e => setGuide({ account_id: e.target.value })}
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg"
                  >
                    <option value="">— Select a guide —</option>
                    {sortPeople(available_guides).map(g => (
                      <option key={g.id} value={g.id}>{g.full_name} ({g.email})</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {data.guide.mode === 'new' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="First name" required value={data.guide.first_name} onChange={v => setGuide({ first_name: v })} />
                  <Field label="Last name" required value={data.guide.last_name} onChange={v => setGuide({ last_name: v })} />
                  <Field label="Email" required type="email" value={data.guide.email} onChange={v => setGuide({ email: v })} />
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">Password<span className="text-[#ED1C24]"> *</span></label>
                    <input
                      type="text"
                      value={data.guide.password}
                      onChange={e => setGuide({ password: e.target.value })}
                      placeholder="min 6 chars"
                      className="w-full px-3 py-2 border border-stone/30 rounded-lg font-mono"
                    />
                    <button type="button" onClick={() => setGuide({ password: generatePassword() })} className="text-xs text-[#ED1C24] hover:underline mt-1">Generate password</button>
                  </div>
                </div>
                <p className="text-xs text-text-muted">If an account with this email already exists, it will be linked as a guide (no password needed). Share the password with new guides out-of-band.</p>
              </div>
            )}
          </fieldset>

          <fieldset>
            <div className="flex items-center justify-between mb-2">
              <legend className="text-base font-semibold text-text-primary">Additional users</legend>
              <button type="button" onClick={addUser} className="text-sm text-[#ED1C24] hover:underline">+ Add user</button>
            </div>
            {data.users.length === 0 ? (
              <p className="text-sm text-text-muted">No additional users. Click "Add user" to create or link more people.</p>
            ) : (
              <div className="space-y-4">
                {/* allow-unsorted-people: do NOT alphabetize. These are draft
                    rows the Super Admin is still typing into, and the edit and
                    remove handlers are addressed by index. */}
                {data.users.map((u, idx) => {
                  const uErr = err(errors, `user_${idx}`)
                  return (
                    <div key={idx} className="border border-stone/20 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-text-muted">User {idx + 1}</span>
                        <button type="button" onClick={() => removeUser(idx)} className="text-xs text-[#ED1C24] hover:underline">Remove</button>
                      </div>
                      {uErr && <div className="px-3 py-2 bg-red-50 text-red-700 rounded text-sm">{uErr}</div>}
                      <div className="grid grid-cols-2 gap-2">
                        <input required placeholder="First name" value={u.first_name} onChange={e => setUser(idx, { first_name: e.target.value })} className="px-3 py-2 border border-stone/30 rounded-lg text-sm" />
                        <input required placeholder="Last name" value={u.last_name} onChange={e => setUser(idx, { last_name: e.target.value })} className="px-3 py-2 border border-stone/30 rounded-lg text-sm" />
                      </div>
                      <input type="email" required placeholder="user@example.com" value={u.email} onChange={e => setUser(idx, { email: e.target.value })} className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm" />
                      <div className="grid grid-cols-3 gap-2">
                        <select value={u.role} onChange={e => setUser(idx, { role: e.target.value })} className="px-3 py-2 border border-stone/30 rounded-lg text-sm">
                          {assignable_roles.map(r => (
                            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                          ))}
                        </select>
                        <input type="text" placeholder="Password (min 6)" value={u.password} onChange={e => setUser(idx, { password: e.target.value })} className="col-span-2 px-3 py-2 border border-stone/30 rounded-lg text-sm font-mono" />
                      </div>
                      <button type="button" onClick={() => setUser(idx, { password: generatePassword() })} className="text-xs text-[#ED1C24] hover:underline">Generate password</button>
                    </div>
                  )
                })}
                <p className="text-xs text-text-muted">New accounts need a password (share it out-of-band). If the email already exists, it is linked to this tenant and keeps its current password.</p>
              </div>
            )}
          </fieldset>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-semibold text-text-primary">
              <input type="checkbox" checked={data.send_welcome_emails} onChange={e => setData('send_welcome_emails', e.target.checked)} aria-describedby="setup-email-scope" />
              Send welcome/invitation emails
            </label>
            <p id="setup-email-scope" className="text-sm text-text-muted">Only the first Admin or Account Owner invitation will be emailed, subject to system email policy. Assigned Guides and additional direct-account users will not be emailed.</p>
            <p className="text-sm text-text-muted">Leave unchecked to create without setup emails. You can explicitly send the invitation afterward; share direct-account credentials separately.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Link href="/super_admin/tenants" className="px-4 py-2 border border-stone/30 rounded-lg text-sm">Cancel</Link>
            <button disabled={processing} type="submit" className="px-4 py-2 bg-[#ED1C24] text-white rounded-lg text-sm font-semibold hover:bg-[#C7161E] disabled:opacity-50">
              {processing ? 'Creating…' : 'Create tenant'}
            </button>
          </div>
        </form>
      </div>
    </SuperAdminLayout>
  )
}

function Field({ label, value, onChange, type = 'text', required, hint }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; hint?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary mb-1">{label}{required && <span className="text-[#ED1C24]"> *</span>}</label>
      <input type={type} required={required} value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 border border-stone/30 rounded-lg" />
      {hint && <p className="text-xs text-text-muted mt-1">{hint}</p>}
    </div>
  )
}
